import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import './Desktop.css';

const Desktop = () => {
  const { t, i18n } = useTranslation();
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth scrolling
  useEffect(() => {
    const handleAnchorClick = (e) => {
      const target = e.target.closest('a[href^="#"]');
      if (!target) return;
      e.preventDefault();
      const element = document.querySelector(target.getAttribute('href'));
      if (element) {
        window.scrollTo({ top: element.offsetTop - 80, behavior: 'smooth' });
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);

  // Dark mode initialization
  useEffect(() => {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const savedTheme = localStorage.getItem('theme');
    const isDark = savedTheme ? savedTheme === 'dark' : prefersDark;
    setDarkMode(isDark);
    document.documentElement.classList.toggle('dark', isDark);

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (!localStorage.getItem('theme')) {
        setDarkMode(e.matches);
        document.documentElement.classList.toggle('dark', e.matches);
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleDarkMode = () => {
    const newDark = !darkMode;
    setDarkMode(newDark);
    document.documentElement.classList.toggle('dark', newDark);
    localStorage.setItem('theme', newDark ? 'dark' : 'light');
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'tr' : 'en';
    i18n.changeLanguage(newLang);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    document.body.style.overflow = !mobileMenuOpen ? 'hidden' : '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const email = e.target.elements.emailInput.value;
    try {
      const response = await fetch("https://api.hazirlan.ai/api/mail-list/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        setFormSuccess(true);
        setFormError('');
      } else {
        const data = await response.json();
        setFormError(data.errors?.email?.[0] || t('newsletter.error'));
      }
    } catch (error) {
      setFormError(t('newsletter.error'));
    }
  };

  const CheckIcon = () => (
    <svg className="w-5 h-5 text-emerald-500 dark:text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );

  return (
    <div className={darkMode ? 'dark' : ''}>
      {/* Main Wrapper - Premium "Akademik Temizlik" Light / "Derin Odak" Dark */}
      <div className="min-h-screen bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">

        {/* ===== HEADER ===== */}
        <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300
          ${isScrolled
            ? 'bg-white/95 dark:bg-slate-900/95 backdrop-blur-md shadow-lg dark:shadow-none border-b border-gray-100 dark:border-slate-800'
            : 'bg-transparent'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 lg:h-20">
              {/* Logo */}
              <a href="/" className="flex-shrink-0">
                <img src="/logo@2x.png" alt="hazirlan.ai" className="h-9 lg:h-11 w-auto dark:brightness-0 dark:invert" />
              </a>

              {/* Desktop Nav */}
              <nav className="hidden md:flex items-center gap-8">
                <a href="#features" className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-primary-400 dark:hover:text-primary-300 transition-colors">
                  {t('nav.features')}
                </a>
                <a href="#pricing" className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-primary-400 dark:hover:text-primary-300 transition-colors">
                  {t('nav.pricing')}
                </a>
                <a href="#faq" className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-primary-400 dark:hover:text-primary-300 transition-colors">
                  {t('nav.faq')}
                </a>
              </nav>

              {/* Desktop Controls */}
              <div className="hidden md:flex items-center gap-2">
                <button
                  onClick={toggleLanguage}
                  className="h-11 px-4 flex items-center justify-center rounded-lg text-sm font-medium
                    text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                >
                  {i18n.language === 'en' ? 'TR' : 'EN'}
                </button>
                <button
                  onClick={toggleDarkMode}
                  className="h-11 w-11 flex items-center justify-center rounded-lg text-xl
                    text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                >
                  {darkMode ? '☀️' : '🌙'}
                </button>
                <a href="#download"
                  className="ml-2 h-11 px-6 flex items-center justify-center rounded-lg text-sm font-semibold
                    bg-primary-400 hover:bg-primary-500 dark:bg-primary-dark dark:hover:bg-primary-300 text-white transition-all
                    shadow-lg shadow-primary-400/25 dark:shadow-primary-dark/25"
                >
                  {t('hero.cta')}
                </a>
              </div>

              {/* Mobile Menu Button */}
              <button onClick={toggleMobileMenu} className="md:hidden h-11 w-11 flex flex-col items-center justify-center gap-1.5">
                <span className={`w-6 h-0.5 bg-gray-900 dark:bg-white transition-all ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
                <span className={`w-6 h-0.5 bg-gray-900 dark:bg-white transition-all ${mobileMenuOpen ? 'opacity-0' : ''}`} />
                <span className={`w-6 h-0.5 bg-gray-900 dark:bg-white transition-all ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
              </button>
            </div>
          </div>
        </header>

        {/* Mobile Menu Overlay */}
        <div className={`fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={toggleMobileMenu} />

        {/* Mobile Menu */}
        <nav className={`fixed top-16 right-0 bottom-0 w-80 max-w-full bg-white dark:bg-slate-900 z-40 md:hidden transition-transform ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex flex-col p-6 gap-2">
            <a href="#features" className="py-3 px-4 text-lg font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800">{t('nav.features')}</a>
            <a href="#pricing" className="py-3 px-4 text-lg font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800">{t('nav.pricing')}</a>
            <a href="#faq" className="py-3 px-4 text-lg font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800">{t('nav.faq')}</a>
            <hr className="my-4 border-gray-200 dark:border-slate-700" />
            <div className="flex items-center justify-between p-4">
              <span className="font-medium text-gray-600 dark:text-gray-400">{t('language.label')}</span>
              <button onClick={toggleLanguage} className="h-11 w-11 flex items-center justify-center bg-gray-100 dark:bg-slate-800 rounded-lg font-medium">
                {i18n.language === 'en' ? 'TR' : 'EN'}
              </button>
            </div>
            <div className="flex items-center justify-between p-4">
              <span className="font-medium text-gray-600 dark:text-gray-400">{t('theme.label')}</span>
              <button onClick={toggleDarkMode} className="h-11 w-11 flex items-center justify-center bg-gray-100 dark:bg-slate-800 rounded-lg text-xl">
                {darkMode ? '☀️' : '🌙'}
              </button>
            </div>
            <a href="#download" className="mt-4 py-4 text-center bg-primary-400 dark:bg-primary-dark text-white font-semibold rounded-lg">
              {t('hero.cta')}
            </a>
          </div>
        </nav>

        {/* ===== HERO SECTION ===== */}
        <section className="relative min-h-screen flex items-center pt-20 pb-16 overflow-hidden
          bg-gradient-to-br from-primary-400 via-primary-500 to-indigo-800 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
          {/* Background Decorations */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-300/20 rounded-full blur-3xl" />
            {/* Grid Pattern */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjAzIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              {/* Hero Content */}
              <div className="flex-1 text-center lg:text-left text-white">
                <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium mb-8 border border-white/20">
                  {t('hero.badge')}
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 leading-tight tracking-tight">
                  {t('hero.title')}
                </h1>

                <p className="text-lg sm:text-xl text-white/80 mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: t('hero.subtitle') }}
                />

                {/* Store Badges */}
                {/* Store Badges */}
                <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                  <a href="https://apps.apple.com/tr/app/haz%C4%B1rlan/id6749094669?l=tr" target="_blank" rel="noopener noreferrer"
                    className="transform hover:scale-105 transition-transform">
                    <img src="/group.svg" alt="App Store" className="h-14" />
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.mmucteba.hazirlan&hl=en" target="_blank" rel="noopener noreferrer"
                    className="transform hover:scale-105 transition-transform">
                    <img src="/googleplay.svg" alt="Google Play" className="h-14" />
                  </a>
                </div>
              </div>

              {/* Hero Image - iPhone Mockup */}
              <div className="flex-1 flex justify-center lg:justify-end">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-300/30 to-purple-400/30 rounded-[3rem] blur-2xl transform scale-110" />
                  <img src="/Home.png" alt="Hazirlan.ai App" className="relative w-64 sm:w-72 lg:w-80 h-auto rounded-[2.5rem] animate-float" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== FEATURE STORY 1: AI Tutor ===== */}
        <section id="features" className="py-20 lg:py-32 bg-gray-50 dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              <div className="w-full lg:w-1/2 flex justify-center lg:order-2">
                <img src="/Luma_empty.png" alt="Luma AI" loading="lazy" className="w-56 sm:w-64 lg:w-72 h-auto rounded-3xl" />
              </div>
              <div className="w-full lg:w-1/2 text-center lg:text-left lg:order-1">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-gray-900 dark:text-white leading-tight">
                  {t('featureStory.aiTutor.title')}
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                  {t('featureStory.aiTutor.description')}
                </p>
                <ul className="space-y-4">
                  {t('featureStory.aiTutor.features', { returnObjects: true }).map((f, i) => (
                    <li key={i} className="flex items-center gap-3 justify-center lg:justify-start">
                      <CheckIcon /><span className="text-gray-700 dark:text-gray-300">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ===== FEATURE STORY 2: Efficiency ===== */}
        <section className="py-20 lg:py-32 bg-white dark:bg-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              <div className="w-full lg:w-1/2 flex justify-center">
                <img src="/SpotBilgi_Study.png" alt="SpotBilgi" loading="lazy" className="w-56 sm:w-64 lg:w-72 h-auto rounded-3xl" />
              </div>
              <div className="w-full lg:w-1/2 text-center lg:text-left">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-gray-900 dark:text-white leading-tight">
                  {t('featureStory.efficiency.title')}
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                  {t('featureStory.efficiency.description')}
                </p>
                <ul className="space-y-4">
                  {t('featureStory.efficiency.features', { returnObjects: true }).map((f, i) => (
                    <li key={i} className="flex items-center gap-3 justify-center lg:justify-start">
                      <CheckIcon /><span className="text-gray-700 dark:text-gray-300">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ===== FEATURE STORY 3: Practice (Center Layout) ===== */}
        <section className="py-20 lg:py-32 bg-gray-50 dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
              {t('featureStory.practice.title')}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-8 leading-relaxed">
              {t('featureStory.practice.description')}
            </p>
            <ul className="flex flex-wrap justify-center gap-6 mb-12">
              {t('featureStory.practice.features', { returnObjects: true }).map((f, i) => (
                <li key={i} className="flex items-center gap-2">
                  <CheckIcon /><span className="text-gray-700 dark:text-gray-300">{f}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-8">
              <img src="/Flashcards.png" alt="Flashcards" loading="lazy" className="w-48 lg:w-56 h-auto rounded-3xl" />
              <img src="/QuestionScreen.png" alt="Questions" loading="lazy" className="w-48 lg:w-56 h-auto rounded-3xl" />
            </div>
          </div>
        </section>

        {/* ===== FEATURE STORY 4: Gamification ===== */}
        <section className="py-20 lg:py-32 bg-white dark:bg-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              <div className="w-full lg:w-1/2 flex justify-center lg:order-2">
                <img src="/AktivitePuanı.png" alt="Activity Points" loading="lazy" className="w-56 sm:w-64 lg:w-72 h-auto rounded-3xl" />
              </div>
              <div className="w-full lg:w-1/2 text-center lg:text-left lg:order-1">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-gray-900 dark:text-white leading-tight">
                  {t('featureStory.gamification.title')}
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                  {t('featureStory.gamification.description')}
                </p>
                <ul className="space-y-4">
                  {t('featureStory.gamification.features', { returnObjects: true }).map((f, i) => (
                    <li key={i} className="flex items-center gap-3 justify-center lg:justify-start">
                      <CheckIcon /><span className="text-gray-700 dark:text-gray-300">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ===== FEATURE STORY 5: Luma'yla Hazırlan ===== */}
        <section className="py-20 lg:py-32 bg-gray-50 dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-gray-900 dark:text-white leading-tight">
                {t('featureStory.lumaHazirlan.title')}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-8 leading-relaxed">
                {t('featureStory.lumaHazirlan.description')}
              </p>
              <ul className="flex flex-wrap justify-center gap-6 mb-12">
                {t('featureStory.lumaHazirlan.features', { returnObjects: true }).map((f, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckIcon /><span className="text-gray-700 dark:text-gray-300">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-8">
              <img src="/pdf_summary.png" alt="PDF Summary" loading="lazy" className="w-48 lg:w-56 h-auto rounded-3xl" />
              <img src="/pdf_summary_pdf.png" alt="PDF Download" loading="lazy" className="w-48 lg:w-56 h-auto rounded-3xl" />
              <img src="/listen_to_note.png" alt="Listen to Notes" loading="lazy" className="w-48 lg:w-56 h-auto rounded-3xl" />
            </div>
          </div>
        </section>

        {/* ===== APP SHOWCASE GALLERY ===== */}
        <section className="py-20 lg:py-32 bg-gray-50 dark:bg-slate-900 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
                {i18n.language === 'tr' ? 'Uygulamayı Keşfet' : 'Explore the App'}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                {i18n.language === 'tr'
                  ? 'TUS hazırlığını kolaylaştıran tüm özellikleri incele'
                  : 'Discover all the features that make TUS preparation easier'}
              </p>
            </div>

            {/* Scrollable Gallery */}
            <div className="flex overflow-x-auto gap-8 pb-8 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {/* MedGPT / Luma Chat - has built-in frame */}
              <div className="flex-shrink-0 snap-center">
                <img src="/medgpt_ss-portrait.png" alt="Luma Chat" loading="lazy" className="w-48 sm:w-56 lg:w-64 h-auto rounded-3xl dark:shadow-none" />
                <p className="text-center mt-4 text-sm font-medium text-gray-700 dark:text-gray-300">Luma AI Chat</p>
              </div>

              {/* Curriculum - has built-in frame */}
              <div className="flex-shrink-0 snap-center">
                <img src="/mufredat-portrait.png" alt="Curriculum" loading="lazy" className="w-48 sm:w-56 lg:w-64 h-auto rounded-3xl dark:shadow-none" />
                <p className="text-center mt-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {i18n.language === 'tr' ? 'Müfredat' : 'Curriculum'}
                </p>
              </div>

              {/* Quick Problem - has built-in frame */}
              <div className="flex-shrink-0 snap-center">
                <img src="/quick_problem-portrait.png" alt="Quick Problem" loading="lazy" className="w-48 sm:w-56 lg:w-64 h-auto rounded-3xl dark:shadow-none" />
                <p className="text-center mt-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {i18n.language === 'tr' ? 'Hızlı Soru' : 'Quick Problem'}
                </p>
              </div>

              {/* Session Stats - has built-in frame */}
              <div className="flex-shrink-0 snap-center">
                <img src="/session_stats-portrait.png" alt="Session Stats" loading="lazy" className="w-48 sm:w-56 lg:w-64 h-auto rounded-3xl dark:shadow-none" />
                <p className="text-center mt-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {i18n.language === 'tr' ? 'Oturum İstatistikleri' : 'Session Stats'}
                </p>
              </div>

              {/* XP Points - has built-in frame */}
              <div className="flex-shrink-0 snap-center">
                <img src="/xp_points-portrait.png" alt="XP Points" loading="lazy" className="w-48 sm:w-56 lg:w-64 h-auto rounded-3xl dark:shadow-none" />
                <p className="text-center mt-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {i18n.language === 'tr' ? 'XP Puanları' : 'XP Points'}
                </p>
              </div>
            </div>

            {/* Scroll hint */}
            <div className="flex justify-center mt-4">
              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <span>←</span>
                {i18n.language === 'tr' ? 'Kaydırarak keşfet' : 'Scroll to explore'}
                <span>→</span>
              </p>
            </div>
          </div>
        </section>

        {/* ===== PRICING SECTION ===== */}
        <section id="pricing" className="py-20 lg:py-32 bg-white dark:bg-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
                {t('pricing.title')}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-4">
                {t('pricing.subtitle')}
              </p>
              <span className="inline-block px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 font-semibold rounded-full text-sm">
                {t('pricing.freeTrial')}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {['monthly', 'threeMonth', 'sixMonth', 'yearly'].map((plan) => {
                const isPopular = plan === 'threeMonth';
                const isBest = plan === 'yearly';
                return (
                  <div key={plan}
                    className={`relative bg-white dark:bg-slate-800 rounded-2xl p-6 lg:p-8 
                      border transition-all duration-300 hover:-translate-y-1
                      ${isPopular
                        ? 'border-primary-dark dark:border-primary-300 dark:shadow-none ring-2 ring-primary-dark/20'
                        : isBest
                          ? 'border-emerald-500 dark:border-emerald-400 shadow-lg dark:shadow-none'
                          : 'border-gray-200 dark:border-slate-700 shadow-lg dark:shadow-none'}`}
                  >
                    {(isPopular || isBest) && (
                      <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 text-xs font-bold text-white rounded-full
                        ${isPopular ? 'bg-primary-400 dark:bg-primary-dark' : 'bg-emerald-600 dark:bg-emerald-500'}`}>
                        {t(`pricing.${plan}.badge`)}
                      </div>
                    )}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {t(`pricing.${plan}.name`)}
                    </h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-gray-900 dark:text-white">₺{t(`pricing.${plan}.monthlyPrice`)}</span>
                      <span className="text-gray-500 dark:text-gray-400">{t(`pricing.${plan}.period`)}</span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{t(`pricing.${plan}.description`)}</p>
                    <ul className="space-y-3 mb-8">
                      {Object.values(t('pricing.premiumFeatures', { returnObjects: true })).map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckIcon /><span className="text-gray-600 dark:text-gray-300">{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button className={`w-full py-3 rounded-xl font-semibold transition-all
                      ${isPopular
                        ? 'bg-primary-400 hover:bg-primary-500 dark:bg-primary-dark dark:hover:bg-primary-300 text-white shadow-lg shadow-primary-400/25'
                        : 'bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-slate-600'}`}>
                      {t(`pricing.${plan}.cta`)}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ===== FAQ SECTION ===== */}
        <section id="faq" className="py-20 lg:py-32 bg-white dark:bg-slate-800">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
                {t('faq.title')}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">{t('faq.subtitle')}</p>
            </div>

            <div className="space-y-4">
              {t('faq.items', { returnObjects: true }).map((item, idx) => (
                <div key={idx} className="bg-gray-50 dark:bg-slate-700 rounded-2xl overflow-hidden border border-gray-100 dark:border-slate-600">
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left min-h-[56px] hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                  >
                    <span className="font-semibold text-gray-900 dark:text-white pr-4">{item.question}</span>
                    <svg className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${openFaqIndex === idx ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className={`transition-all duration-300 overflow-hidden ${openFaqIndex === idx ? 'max-h-96 pb-5' : 'max-h-0'}`}>
                    <p className="px-5 text-gray-600 dark:text-gray-300 leading-relaxed">{item.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== DOWNLOAD / CTA SECTION ===== */}
        <section id="download" className="py-20 lg:py-32 bg-gradient-to-br from-primary-400 via-primary-500 to-indigo-800">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">{t('newsletter.title')}</h2>
            <p className="text-lg text-white/80 mb-10">{t('newsletter.subtitle')}</p>

            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <a href="https://apps.apple.com/tr/app/haz%C4%B1rlan/id6749094669?l=tr" target="_blank" rel="noopener noreferrer" className="transform hover:scale-105 transition-transform">
                <img src="/group.svg" alt="App Store" className="h-14" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.mmucteba.hazirlan&hl=en" target="_blank" rel="noopener noreferrer" className="transform hover:scale-105 transition-transform">
                <img src="/googleplay.svg" alt="Google Play" className="h-14" />
              </a>
            </div>
          </div>
        </section>

        {/* ===== FOOTER ===== */}
        <footer className="bg-gray-900 dark:bg-black text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
              <div className="md:col-span-2">
                <img src="/logo@2x.png" alt="hazirlan.ai" className="h-10 mb-4 brightness-0 invert" />
                <p className="text-gray-400 mb-4 max-w-sm leading-relaxed">{t('footer.description')}</p>
                <a href="mailto:iletisim@hazirlan.ai" className="text-primary-300 hover:text-blue-300 transition-colors">iletisim@hazirlan.ai</a>
              </div>
              <div>
                <h3 className="font-semibold mb-4 text-white">{t('footer.legal')}</h3>
                <ul className="space-y-3">
                  <li><Link to="/kvkk" className="text-gray-400 hover:text-white transition-colors">KVKK Aydınlatma Metni</Link></li>
                  <li><Link to="/eula" className="text-gray-400 hover:text-white transition-colors">Kullanıcı Sözleşmesi (EULA)</Link></li>
                  <li><Link to="/kvkk" className="text-gray-400 hover:text-white transition-colors">Gizlilik Politikası</Link></li>
                  <li><button className="text-gray-400 hover:text-white transition-colors text-left">Çerez (Cookie) Ayarları</button></li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-4 text-white">{t('footer.social')}</h3>
                <div className="flex gap-4">
                  <a href="https://www.instagram.com/hazirlan.ai/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg hover:bg-white/20 transition-colors">
                    <img src="/social-icons-2.svg" alt="Instagram" className="w-5 h-5" />
                  </a>
                  <a href="https://www.linkedin.com/company/hazirlan-ai/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg hover:bg-white/20 transition-colors">
                    <img src="/social-icons-3.svg" alt="LinkedIn" className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500 text-sm">
              {t('footer.copyright')}
            </div>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default Desktop;